import * as React from "react";
import Icon from "../Icon";
import styles from "./parts.module.scss";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
// import "@culturehq/add-to-calendar/dist/styles.css";
import {
  EventPartsQuery,
  GetAlbumDetailsQuery,
  GetLiveScheduleQuery,
} from "@src/@types/api.d";
import { EventPartViewModel, useEventParts } from "@src/hooks/useEventParts";
import DialogModal from "../Dialog";
import LiveSchedules from "../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";

export type IParts = {
  eventId: number;
  parts?: EventPartViewModel[];
  eventPartsData?: EventPartsQuery;
  liveSchedules?: GetLiveScheduleQuery;
  sectionImage?: string;
  eventTimezone?: string | null;
  isInvitedToEvent?: boolean;
  albumDetailsData?: GetAlbumDetailsQuery;
  partsImageData?: {
    getPartsImage?: string;
  };
};

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);
  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className={styles["home__event-types-container"]}>
      {visibleParts.length === 0 ? (
        <></>
      ) : (
        <motion.div
          initial={{ opacity: 1 }}
          whileInView={{
            opacity: 1,
            transition: {
              delayChildren: 0.5,
              staggerChildren: 0.5,
            },
          }}
          className={styles["home__event-types"]}
        >
          {visibleParts.map((item, idx) => {
            return (
              <motion.div
                initial={{ opacity: 0, y: -15 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.2,
                    duration: 1,
                    ease: [0.79, 0.14, 0.15, 0.86],
                  },
                }}
                key={`home action ${idx}`}
                className={styles["home__event-types-box"]}
              >
                <div className={styles["home__event-types-icon"]}>
                  <Icon iconName="roses" />
                </div>
                <h1 className={styles["home__event-types-heading"]}>
                  {item.name}
                </h1>
                <p className={styles["home__event-types-date"]}>
                  {item.formattedDate}
                </p>
                <p className={styles["home__event-types-time"]}>
                  {item.formattedTime}
                </p>
                {item.timeZoneLabel ? (
                  <p className={styles["home__event-types-time"]}>
                    {item.timeZoneLabel}
                  </p>
                ) : null}
                {item.showAddress ? (
                  <p className={styles["home__event-types-time"]}>
                    {item?.address}
                  </p>
                ) : (
                  <></>
                )}
                {item.showAddress && item.hasLiveStream && liveSchedules ? (
                  <DialogModal
                    fillScreen={true}
                    trigger={
                      <button className="bg-inawo-gold-300 rounded-[0.125rem] h-[2.75rem] px-[1.5rem] flex items-center gap-[0.5rem] border-solid !border border-black mb-[0.75rem] hover:opacity-90 ease-in-out duration-300 transition-opacity">
                        <Icon iconName="videoCamera" />
                        <span className="">Watch Live</span>
                      </button>
                    }
                  >
                    <LiveSchedules schedules={liveSchedules} />
                  </DialogModal>
                ) : (
                  <></>
                )}
                <div className="mb-3">
                  {item.showAddress && (
                    <AddToCalendar
                      event={{
                        name: item.name,
                        details: null,
                        location: item?.address,
                        startsAt: item?.time,
                        endsAt: addOneDay(item?.time),
                      }}
                    >
                      <span>Save Date</span>
                    </AddToCalendar>
                  )}
                </div>
                {item.showMapAction &&
                  item.latitude != null &&
                  item.longitude != null && (
                    <div>
                      <DialogModal
                        trigger={
                          <button className="bg-transparent rounded-[5px] h-[3rem] px-[1.5rem] flex items-center gap-[0.5rem] border-solid !border border-black mb-[0.75rem] hover:opacity-90 ease-in-out duration-300 transition-opacity">
                            <span className="">View on Map</span>
                          </button>
                        }
                        open={openMap}
                        onOpenChange={setOpenMap}
                      >
                        <div className="max-w-[500px] h-auto bg-white rounded-[5px] p-[20px]">
                          <div className="mb-4 flex items-center justify-center gap-[5px]">
                            <Icon iconName="location" />
                            <h2 className="font-bold text-[18px] text-center">
                              {item?.address}
                            </h2>
                          </div>
                          {!isLoaded ? (
                            <h3>Loading Map.....</h3>
                          ) : (
                            <div>
                              <div className="w-[100%] h-[350px] mb-4">
                                <GoogleMap
                                  center={{
                                    lat: item.latitude,
                                    lng: item.longitude,
                                  }}
                                  mapContainerStyle={{
                                    width: "100%",
                                    height: "100%",
                                  }}
                                  zoom={17}
                                >
                                  <MarkerF
                                    position={{
                                      lat: item.latitude,
                                      lng: item.longitude,
                                    }}
                                    onLoad={onLoadMarker}
                                  />
                                </GoogleMap>
                              </div>
                              <div className="flex">
                                <button
                                  onClick={() =>
                                    handleViewDirections({
                                      latitude: item.latitude as number,
                                      longitude: item.longitude as number,
                                    })
                                  }
                                  className="bg-transparent rounded-[5px] mx-auto h-[3rem] px-[1.5rem] flex items-center gap-[0.5rem] border-solid !border border-black hover:opacity-90 ease-in-out duration-300 transition-opacity"
                                >
                                  <span className="">View directions</span>
                                </button>
                                <button
                                  onClick={() =>
                                    handleFindHotelsNearby({
                                      latitude: item.latitude as number,
                                      longitude: item.longitude as number,
                                    })
                                  }
                                  className="bg-transparent rounded-[5px] mx-auto h-[3rem] px-[1.5rem] flex items-center gap-[0.5rem] border-solid !border border-black hover:opacity-90 ease-in-out duration-300 transition-opacity"
                                >
                                  <span className="">Find hotels around</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </DialogModal>
                    </div>
                  )}{" "}
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  ) : (
    <></>
  );
};

export default Parts;
